import type { Rng } from '../../core/rng';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { Lot } from './types';
import type { HouseStreets, StreetFront, StreetKind } from './houseStreets';
import type { ShopFronts } from './shops';
import type { PartyWalls, Stretch } from './partyWalls';
import { openStretches } from './openings';
import { boxSolid } from './solids';

/** How far a house's stoepen reach out from its front and side walls, 0 where it has none, and their height. */
export interface StoepPlan {
  readonly front: number;
  readonly left: number;
  readonly right: number;
  readonly height: number;
  /** How far below the floor the step is built, where the street falls away under it (foundation.stoepFoot). */
  readonly foot?: number;
}

export const NO_STOEP: StoepPlan = { front: 0, left: 0, right: 0, height: 0 };

/* Sources and estimates: docs/plans/houses.md, "Stoepen". */
export const STOEP = {
  /** 3 voet into a street and 4 into a canal quay: the most the ordinance of 1565 allowed, confirming older practice (S16-235, S18). */
  reach: { street: 0.85, square: 0.85, shore: 0.85, quay: 1.15 } as Readonly<Partial<Record<StreetKind, number>>>,
  /** A stoep takes at most this share of its way's width, so a narrow street keeps its middle. */
  ofWidth: 0.2,
  /** Narrower than this, a stoep would be a kerb. */
  least: 0.4,
  /** One step: medieval houses opened straight into the front room (S18). The height is an estimate. */
  height: { min: 0.12, max: 0.16 },
  /** Houses that keep a stoep without a shop: an estimate. Shops keep one where the way is wide enough (S16-214, S21, S28). */
  share: 0.5,
  /** A corner house's stoep turns the corner (S28) and runs this far along the side street, where the wall stands open. */
  side: 3.5,
  /** Shorter than this, the side stoep is left out and the front one keeps to the front. */
  sideLeast: 1.0,
} as const;

/** The furthest any stoep reaches out from its wall. */
export const STOEP_REACH_MOST = Math.max(...Object.values(STOEP.reach));

export function stoepReach(street: StreetFront | null): number {
  if (!street) return 0;
  const most = STOEP.reach[street.kind] ?? 0;
  const reach = street.width === null ? most : Math.min(most, STOEP.ofWidth * street.width);
  return reach >= STOEP.least ? reach : 0;
}

/** From its own fork of the house's stream, so a stoep moves no other parameter of any house. */
export function sampleStoep(lot: Pick<Lot, 'kind'>, streets: HouseStreets, shop: ShopFronts, rng: Rng): StoepPlan {
  const front = lot.kind === 'house' ? stoepReach(streets.front) : 0;
  if (front === 0) return NO_STOEP;
  const own = rng.fork('stoep');
  const kept = own.chance(STOEP.share) || shop.front;
  if (!kept) return NO_STOEP;
  const height = own.range(STOEP.height.min, STOEP.height.max);
  return { front, left: stoepReach(streets.left), right: stoepReach(streets.right), height };
}

/** How far back from its front corner a side wall `length` long stands open, before a neighbour's wall covers it. */
export function openFromFront(length: number, covered: readonly Stretch[]): number {
  const atFront = openStretches(length, covered).find(([, to]) => to >= length);
  return atFront ? length - atFront[0] : 0;
}

/** Where the stoepen stand: the ground floor's front, back from the lot's front line, and its side walls. */
export interface StoepWalls {
  readonly front: number;
  readonly half: number;
  /** How far back from the lot's front line each side wall stands open (openFromFront). */
  readonly open: { readonly left: number; readonly right: number };
}

/** The walls of a house whose ground floor stands `front` back from its lot's front line, on a lot `depth` deep. */
export function stoepWalls(front: number, half: number, depth: number, party: PartyWalls): StoepWalls {
  const open = { left: openFromFront(depth, party.covered.left), right: openFromFront(depth, party.covered.right) };
  return { front, half, open };
}

/* How far back along a side wall its stoep runs, from the ground floor's front; 0 for none. */
function sideRun(reach: number, open: number, front: number): number {
  const run = Math.min(front + STOEP.side, open) - front;
  return reach > 0 && run >= STOEP.sideLeast ? run : 0;
}

/**
 * The stoep before the ground floor, and on a corner the one along the side street, which the front
 * one reaches round to meet. Each is a step closed but for its foot, which stands on the street.
 */
export function buildStoepen(builder: MeshBuilder, style: VertexStyle, frame: Frame, stoep: StoepPlan, walls: StoepWalls): void {
  if (stoep.front === 0) return;
  const { front, half, open } = walls;
  const left = sideRun(stoep.left, open.left, front);
  const right = sideRun(stoep.right, open.right, front);
  const [zLeft, zRight] = [-half - (left > 0 ? stoep.left : 0), half + (right > 0 ? stoep.right : 0)];
  const low = -(stoep.foot ?? 0);
  boxSolid(builder, style, frame, [front - stoep.front, low, zLeft], [front, stoep.height, zRight]);
  if (left > 0) boxSolid(builder, style, frame, [front, low, zLeft], [front + left, stoep.height, -half]);
  if (right > 0) boxSolid(builder, style, frame, [front, low, half], [front + right, stoep.height, zRight]);
}
